import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { X, Check, AlertCircle, Pencil, Wrench } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { EditJobsModal } from './EditJobsModal';
import { Checkbox } from '@/components/ui/checkbox';

interface ParsedTicket {
  ticketNumber: string;
  year?: number;
  make?: string;
  model?: string;
  engineSize?: string;
  customerName: string;
  customerPhone?: string;
  jobs: Array<{
    category: string;
    submenu: string;
    name: string;
    minutes: number;
    customHours?: number;
    notes?: string;
    bay: string;
    skill: number;
  }>;
  assignedTechnician?: string;
  startTime?: string;
  appointmentDate: string;
  status: string;
  isWaiter?: boolean;
  // VPV-A2: dropped `vehiclePresent?: boolean` from CSV ParsedTicket.
  // The accompanying CSV verification checkbox (formerly bound to this
  // field) was an orphan UI control — its value was never persisted to
  // `tickets.vehicle_present`. The DB column is canonically derived by
  // the `sync_vehicle_present` trigger (migration `20260412222311`)
  // from `parking_lot OR parking_spot_id OR bay_id OR
  // timer_status IN ('running','paused')`, and `ImportJobs.tsx`'s
  // ticket-insert payload has explicitly excluded `vehicle_present`
  // since the trigger landed (Codex §5; see ImportJobs.tsx L525).
  // Per VPV-A1 audit doctrine: vehicle physical presence must be
  // derived from actual location/workflow truth, not a standalone CSV
  // verification toggle. Mirrors Phase 9 TicketKebabMenu manual-toggle
  // removal precedent (TicketKebabMenu.tsx:713-714).
}

interface CSVTicketVerificationProps {
  tickets: ParsedTicket[];
  onConfirm: (tickets: ParsedTicket[]) => void;
  onCancel: () => void;
}

export const CSVTicketVerification = ({ tickets, onConfirm, onCancel }: CSVTicketVerificationProps) => {
  const [editedTickets, setEditedTickets] = useState<ParsedTicket[]>(
    tickets.map(t => ({
      ...t,
      isWaiter: t.isWaiter ?? Boolean(t.startTime),
    }))
  );
  const [expandedTickets, setExpandedTickets] = useState<Set<number>>(new Set([0]));
  const [editingJobsIndex, setEditingJobsIndex] = useState<number | null>(null);

  const toggleTicket = (index: number) => {
    const newExpanded = new Set(expandedTickets);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedTickets(newExpanded);
  };

  const updateTicket = (index: number, field: string, value: any) => {
    const updated = [...editedTickets];
    updated[index] = { ...updated[index], [field]: value };
    setEditedTickets(updated);
  };

  const updateJob = (ticketIndex: number, jobIndex: number, field: string, value: any) => {
    const updated = [...editedTickets];
    const jobs = [...(updated[ticketIndex].jobs || [])];
    jobs[jobIndex] = { ...jobs[jobIndex], [field]: value };
    updated[ticketIndex] = { ...updated[ticketIndex], jobs };
    setEditedTickets(updated);
  };

  const removeJob = (ticketIndex: number, jobIndex: number) => {
    const updated = [...editedTickets];
    const jobs = [...(updated[ticketIndex].jobs || [])];
    jobs.splice(jobIndex, 1);
    updated[ticketIndex] = { ...updated[ticketIndex], jobs };
    setEditedTickets(updated);
  };

  const removeTicket = (index: number) => {
    setEditedTickets(editedTickets.filter((_, i) => i !== index));
  };

  const hasIssues = (ticket: ParsedTicket) => {
    return !ticket.year || !ticket.make || !ticket.model || !ticket.customerName || !ticket.jobs?.length;
  };

  const handleSaveJobs = (ticketIndex: number, jobs: any[]) => {
    const updated = [...editedTickets];
    updated[ticketIndex] = { ...updated[ticketIndex], jobs };
    setEditedTickets(updated);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Verify Import Data</h2>
          <p className="text-muted-foreground">
            Review and edit {editedTickets.length} ticket{editedTickets.length !== 1 ? 's' : ''} before importing
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(editedTickets)} disabled={editedTickets.length === 0}>
            <Check className="mr-2 h-4 w-4" />
            Import {editedTickets.length} Ticket{editedTickets.length !== 1 ? 's' : ''}
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {editedTickets.map((ticket, ticketIndex) => (
          <Card key={ticketIndex} className={hasIssues(ticket) ? 'border-amber-500' : ''}>
            <Collapsible
              open={expandedTickets.has(ticketIndex)}
              onOpenChange={() => toggleTicket(ticketIndex)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center gap-3 cursor-pointer flex-1">
                      <Pencil className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1">
                        <CardTitle className="text-lg">
                          Ticket #{ticket.ticketNumber}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">
                          {ticket.year} {ticket.make} {ticket.model}
                          {ticket.customerName && ` - ${ticket.customerName}`}
                        </p>
                      </div>
                      {hasIssues(ticket) && (
                        <Badge variant="outline" className="border-amber-500 text-amber-600">
                          <AlertCircle className="mr-1 h-3 w-3" />
                          Missing Info
                        </Badge>
                      )}
                      <Badge variant="secondary">
                        {ticket.jobs?.length || 0} job{ticket.jobs?.length !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  </CollapsibleTrigger>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeTicket(ticketIndex)}
                    className="ml-2"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>

              <CollapsibleContent>
                <CardContent className="space-y-4">
                  <Separator />
                  
                  {/* Vehicle Information */}
                  <div>
                    <h4 className="font-semibold mb-3">Vehicle Information</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Year *</Label>
                        <Input
                          type="number"
                          value={ticket.year || ''}
                          onChange={(e) => updateTicket(ticketIndex, 'year', parseInt(e.target.value))}
                          className={!ticket.year ? 'border-amber-500' : ''}
                        />
                      </div>
                      <div>
                        <Label>Make *</Label>
                        <Input
                          value={ticket.make || ''}
                          onChange={(e) => updateTicket(ticketIndex, 'make', e.target.value)}
                          className={!ticket.make ? 'border-amber-500' : ''}
                        />
                      </div>
                      <div>
                        <Label>Model *</Label>
                        <Input
                          value={ticket.model || ''}
                          onChange={(e) => updateTicket(ticketIndex, 'model', e.target.value)}
                          className={!ticket.model ? 'border-amber-500' : ''}
                        />
                      </div>
                      <div>
                        <Label>Engine Size</Label>
                        <Input
                          value={ticket.engineSize || ''}
                          onChange={(e) => updateTicket(ticketIndex, 'engineSize', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Customer Information */}
                  <div>
                    <h4 className="font-semibold mb-3">Customer Information</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Customer Name *</Label>
                        <Input
                          value={ticket.customerName || ''}
                          onChange={(e) => updateTicket(ticketIndex, 'customerName', e.target.value)}
                          className={!ticket.customerName ? 'border-amber-500' : ''}
                        />
                      </div>
                      <div>
                        <Label>Customer Phone</Label>
                        <Input
                          value={ticket.customerPhone || ''}
                          onChange={(e) => updateTicket(ticketIndex, 'customerPhone', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Schedule Information */}
                  <div>
                    <h4 className="font-semibold mb-3">Schedule</h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Date</Label>
                          <Input
                            type="date"
                            value={ticket.appointmentDate || ''}
                            onChange={(e) => updateTicket(ticketIndex, 'appointmentDate', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Start Time</Label>
                          <Input
                            type="time"
                            value={ticket.startTime || ''}
                            onChange={(e) => updateTicket(ticketIndex, 'startTime', e.target.value)}
                          />
                        </div>
                      </div>
                      {/* VPV-A2: removed standalone "Vehicle Present"
                          checkbox — orphan UI control whose value never
                          persisted (DB column is trigger-derived from
                          location + timer state, see
                          `sync_vehicle_present` migration
                          20260412222311). */}
                      <div className="flex items-center gap-6">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`waiter-${ticketIndex}`}
                            checked={ticket.isWaiter || false}
                            onCheckedChange={(checked) => updateTicket(ticketIndex, 'isWaiter', checked)}
                          />
                          <Label htmlFor={`waiter-${ticketIndex}`} className="cursor-pointer">
                            Waiter
                          </Label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Assigned Technician */}
                  {ticket.assignedTechnician && (
                    <div>
                      <h4 className="font-semibold mb-3">Assignment</h4>
                      <div>
                        <Label>Assigned Technician</Label>
                        <Input
                          value={ticket.assignedTechnician || ''}
                          onChange={(e) => updateTicket(ticketIndex, 'assignedTechnician', e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  {/* Jobs */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold">
                        Jobs ({ticket.jobs?.length || 0})
                        {!ticket.jobs?.length && <span className="text-amber-600 text-sm ml-2">* At least one job required</span>}
                      </h4>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setEditingJobsIndex(ticketIndex)}
                      >
                        <Wrench className="h-4 w-4 mr-2" />
                        Edit Jobs
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {ticket.jobs?.map((job, jobIndex) => (
                        <div key={jobIndex} className="flex items-start gap-2 p-3 border rounded-lg">
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            <div className="col-span-2">
                              <Label className="text-xs">Job Name</Label>
                              <p className="font-medium">{job.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {job.category} / {job.submenu}
                              </p>
                            </div>
                            <div>
                              <Label className="text-xs">Hours</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={job.customHours || job.minutes / 60}
                                onChange={(e) => {
                                  const hours = parseFloat(e.target.value);
                                  updateJob(ticketIndex, jobIndex, 'customHours', hours);
                                  updateJob(ticketIndex, jobIndex, 'minutes', Math.round(hours * 60));
                                }}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Bay</Label>
                              <Input
                                value={job.bay}
                                onChange={(e) => updateJob(ticketIndex, jobIndex, 'bay', e.target.value)}
                                className="h-8"
                              />
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeJob(ticketIndex, jobIndex)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        ))}
      </div>

      {editedTickets.length === 0 && (
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">No tickets to import</p>
          <Button variant="outline" onClick={onCancel} className="mt-4">
          Go Back
        </Button>
      </Card>
    )}

    {editingJobsIndex !== null && (
      <EditJobsModal
        open={editingJobsIndex !== null}
        onOpenChange={(open) => !open && setEditingJobsIndex(null)}
        jobs={editedTickets[editingJobsIndex]?.jobs || []}
        onSave={(jobs) => handleSaveJobs(editingJobsIndex, jobs)}
      />
    )}
  </div>
  );
};
